/** Legacy global roles retained for dual-read during C1A→C1B cutover. */
export const ROLES = ["owner", "publisher", "editor", "auditor"];
export const MFA_REQUIRED_ROLES = ["owner", "publisher"];
export const MFA_REQUIRED_PLATFORM_ROLES = ["owner"];
export const MFA_REQUIRED_TENANT_ROLES = ["tenant_admin", "publisher"];
export function roleRequiresMfa(role) {
    return MFA_REQUIRED_ROLES.includes(role);
}
export function platformRoleRequiresMfa(role) {
    return role != null && MFA_REQUIRED_PLATFORM_ROLES.includes(role);
}
export function tenantRoleRequiresMfa(role) {
    return role != null && MFA_REQUIRED_TENANT_ROLES.includes(role);
}
/** MFA required when platform owner, tenant admin/publisher, or legacy owner/publisher. */
export function authContextRequiresMfa(input) {
    if (platformRoleRequiresMfa(input.platformRole))
        return true;
    if (tenantRoleRequiresMfa(input.membershipRole))
        return true;
    if (input.legacyRole && roleRequiresMfa(input.legacyRole))
        return true;
    return false;
}
export const ROLE_PERMISSIONS = {
    owner: [
        "users.manage",
        "settings.manage",
        "sites.manage",
        "sites.transfer",
        "groups.manage",
        "content.create",
        "content.edit",
        "content.publish",
        "content.schedule",
        "content.rollback",
        "content.archive",
        "audit.read",
        "audit.export",
        "portal.bundle.manage",
        "portal.bundle.generate",
        "reseller.profile.read",
        "reseller.profile.manage",
        "reseller.profile.publish",
        "reseller.site.assign",
        "org.create",
        "org.activate",
        "org.suspend",
        "org.archive",
        "org.membership.manage",
        "org.entitlements.read",
        "org.entitlements.manage",
        "fleet.inventory.read",
        "fleet.inventory.manage",
        "fleet.deployment.record",
        "fleet.deployment.manage",
    ],
    publisher: [
        "content.create",
        "content.edit",
        "content.publish",
        "content.schedule",
        "content.rollback",
        "content.archive",
        "audit.read",
        "portal.bundle.generate",
        "reseller.profile.read",
        "reseller.profile.publish",
        "fleet.inventory.read",
        "fleet.deployment.record",
    ],
    editor: [
        "content.create",
        "content.edit",
        "content.archive",
        "audit.read",
        "reseller.profile.read",
    ],
    auditor: ["audit.read", "reseller.profile.read", "fleet.inventory.read"],
};
/** Platform operator: fleet support without tenant content publishing (C5 D3/D7). */
export const PLATFORM_OPERATOR_PERMISSIONS = [
    "fleet.inventory.read",
    "fleet.deployment.record",
    "audit.read",
];
/** Tenant-scoped membership permissions (C1A). Platform-only perms are omitted. */
export const TENANT_ROLE_PERMISSIONS = {
    tenant_admin: [
        "sites.manage",
        "groups.manage",
        "content.create",
        "content.edit",
        "content.publish",
        "content.schedule",
        "content.rollback",
        "content.archive",
        "audit.read",
        "portal.bundle.generate",
        "reseller.profile.read",
        "reseller.profile.manage",
        "reseller.profile.publish",
        "reseller.site.assign",
        "org.membership.manage",
        "org.entitlements.read",
        "fleet.inventory.read",
        "fleet.inventory.manage",
        "fleet.deployment.record",
        "fleet.deployment.manage",
    ],
    publisher: [
        "content.create",
        "content.edit",
        "content.publish",
        "content.schedule",
        "content.rollback",
        "content.archive",
        "audit.read",
        "portal.bundle.generate",
        "reseller.profile.read",
        "reseller.profile.publish",
        "org.entitlements.read",
        "fleet.inventory.read",
        "fleet.deployment.record",
    ],
    editor: [
        "content.create",
        "content.edit",
        "content.archive",
        "audit.read",
        "reseller.profile.read",
        "org.entitlements.read",
    ],
    auditor: ["audit.read", "reseller.profile.read", "org.entitlements.read", "fleet.inventory.read"],
};
export function hasPermission(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
export function hasTenantPermission(role, permission) {
    return TENANT_ROLE_PERMISSIONS[role].includes(permission);
}
export function hasPlatformOwnerPermission(permission) {
    return hasPermission("owner", permission);
}
export function hasPlatformOperatorPermission(permission) {
    return PLATFORM_OPERATOR_PERMISSIONS.includes(permission);
}
/**
 * Dual-read authorization for C1A:
 * platform owner → full owner permissions;
 * platform operator → fleet/support permissions only;
 * else active membership role → tenant permissions;
 * else legacy admin_users.role.
 */
export function canPerform(input) {
    if (input.platformRole === "owner") {
        return hasPlatformOwnerPermission(input.permission);
    }
    if (input.platformRole === "operator") {
        return hasPlatformOperatorPermission(input.permission);
    }
    if (input.membershipRole) {
        return hasTenantPermission(input.membershipRole, input.permission);
    }
    if (input.legacyRole) {
        return hasPermission(input.legacyRole, input.permission);
    }
    return false;
}
/** Map legacy publisher/editor/auditor to tenant membership roles. */
export function legacyRoleToTenantRole(role) {
    if (role === "publisher")
        return "publisher";
    if (role === "editor")
        return "editor";
    if (role === "auditor")
        return "auditor";
    return null;
}
//# sourceMappingURL=roles.js.map